/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'

import { EuiPanel, EuiSkeletonText, EuiSpacer, EuiTitle } from '@elastic/eui'

import type { CloudResource } from '@modules/billing-api/v1/types'
import { FormattedMessage } from '@modules/i18n'

import BalanceInfo from '../../../components/BalanceInfo'
import { getBalanceData } from '../utils'

import type { Budget, Costs } from '../../types'

type Props = {
  budget: Budget
  cloudResources: CloudResource[] | undefined
  costs: Costs | undefined
}

const Balance: FunctionComponent<Props> = ({ budget, cloudResources, costs }) => {
  const isCloudResource = budget.scope_type === 'cloud_resource'
  const isLoading = !costs || (isCloudResource && !cloudResources)

  return (
    <EuiPanel color='subdued' paddingSize='m' hasBorder={false} hasShadow={false}>
      <EuiTitle size='xxs'>
        <h3>
          <FormattedMessage
            id='billing.notifications.budgets.budget-details.balance.title'
            defaultMessage='Current vs Budgeted (ECU)'
          />
        </h3>
      </EuiTitle>
      <EuiSpacer size='xs' />

      <EuiSkeletonText lines={1} isLoading={isLoading}>
        <BalanceInfo {...getBalanceData({ budget, cloudResources, costs })} />
      </EuiSkeletonText>
    </EuiPanel>
  )
}

export default Balance
